import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  Upload, FileText, X, Loader2, Clock, CheckCircle, AlertCircle, Search, Trash2,
  Folder, FolderPlus, ArrowLeft, Pencil, RefreshCw, Sparkles, Bot, Play
} from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { db } from '../firebase';
import { onSnapshot, collection, query, orderBy } from 'firebase/firestore';
import { GLOBAL_BACKEND_URL } from '../config';

const BASE_URL = GLOBAL_BACKEND_URL;

// ── Outside Dashboard to prevent remount flashing ────────────────────────────

const SkeletonCard = () => (
  <div className="grid grid-cols-[1fr_120px_100px_140px_100px] gap-4 px-6 py-4 animate-pulse">
    <div className="flex items-center gap-3">
      <div className="w-9 h-9 rounded-lg bg-gray-100 flex-shrink-0" />
      <div className="space-y-1.5">
        <div className="h-3.5 w-48 rounded bg-gray-200" />
        <div className="h-3 w-16 rounded bg-gray-100" />
      </div>
    </div>
    <div className="h-3.5 w-20 rounded bg-gray-100 self-center" />
    <div className="h-3.5 w-12 rounded bg-gray-100 self-center" />
    <div className="h-6 w-20 rounded-full bg-gray-100 self-center" />
    <div className="h-7 w-14 rounded-lg bg-gray-100 self-center ml-auto" />
  </div>
);

const DocumentRow = React.memo(({ doc, onView, onDelete, getStatusIcon, getStatusBadge }) => (
  <div className="grid grid-cols-[1fr_120px_100px_140px_100px] gap-4 px-6 py-4 items-center hover:bg-gray-50/50 transition-colors group">
    {/* Name */}
    <div className="flex items-center gap-3 min-w-0">
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
        doc.file_type?.toLowerCase().includes('pdf')
          ? 'bg-red-50 text-red-500'
          : doc.file_type?.toLowerCase().match(/jpg|jpeg|png|tiff?|bmp|heic|heif|webp/)
            ? 'bg-blue-50 text-blue-500'
            : 'bg-gray-100 text-gray-400'
      }`}>
        <FileText className="h-4 w-4" />
      </div>
      <div className="min-w-0">
        <p className="text-sm font-medium text-gray-900 truncate">{doc.original_filename}</p>
        <p className="text-xs text-gray-400 truncate">
          <span className="uppercase">{doc.file_type || '—'}</span>
          {(doc.model_used || doc.model) && ` · ${doc.model_used || doc.model}`}
        </p>
      </div>
    </div>

    {/* Date */}
    <span className="text-sm text-gray-500">
      {doc.upload_timestamp
        ? new Date(doc.upload_timestamp.toDate ? doc.upload_timestamp.toDate() : doc.upload_timestamp)
            .toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
        : '—'}
    </span>

    {/* Size */}
    <span className="text-sm text-gray-500">
      {doc.file_size ? `${(doc.file_size / 1024 / 1024).toFixed(1)} MB` : '—'}
    </span>

    {/* Status */}
    <div>
      {doc.processing_status && doc.processing_status !== 'completed' && doc.processing_status !== 'failed' ? (
        <div className="space-y-1">
          <div className="flex items-center gap-1.5">
            <Loader2 className="h-3 w-3 animate-spin text-amber-500 flex-shrink-0" />
            <span className="text-xs font-medium text-amber-600 truncate">
              {doc.processing_step || 'Processing...'}
            </span>
          </div>
          {doc.current_page && doc.total_pages && (
            <div className="w-full h-1 bg-gray-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-amber-400 rounded-full transition-all duration-500 ease-out"
                style={{ width: `${(doc.current_page / doc.total_pages) * 100}%` }}
              />
            </div>
          )}
        </div>
      ) : (
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${getStatusBadge(doc.processing_status)}`}>
          {getStatusIcon(doc.processing_status)}
          {doc.processing_status || 'Unknown'}
        </span>
      )}
    </div>

    {/* Actions */}
    <div className="flex justify-end items-center gap-1.5">
      {doc.processing_status === 'completed' && (
        <button
          onClick={() => onView(doc)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
        >
          <FileText className="h-3.5 w-3.5" />
          View
        </button>
      )}
      <button
        onClick={(e) => onDelete(doc, e)}
        className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
        title="Delete document"
      >
        <Trash2 className="h-3.5 w-3.5" />
      </button>
    </div>
  </div>
));

// ── Helpers ──────────────────────────────────────────────────────────────────

const naturalCompare = (a, b) =>
  (a || '').localeCompare(b || '', undefined, { numeric: true, sensitivity: 'base' });

const formatDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString('es-PE', { day: 'numeric', month: 'short', year: 'numeric' }) : '';

const folderTitle = (folder) => {
  if (!folder) return 'Folder';
  if (folder.name) return folder.name;
  return folder.created_at ? `Sin nombre · ${formatDate(folder.created_at)}` : 'Sin nombre';
};

const SUMMARY_LABELS = {
  generating: 'Escribiendo resumen…',
  ready: 'Resumen listo',
  failed: 'Error en el resumen',
};

// ── Main component ────────────────────────────────────────────────────────────

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const folderId = searchParams.get('folder');

  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadFiles, setUploadFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const [models, setModels] = useState([]);
  const [selectedModel, setSelectedModel] = useState('');
  const [folders, setFolders] = useState([]);
  const [folder, setFolder] = useState(null);
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState('');
  const [analyses, setAnalyses] = useState([]);
  const [analysisModel, setAnalysisModel] = useState('');
  const [analysisInstructions, setAnalysisInstructions] = useState('');

  // Authenticated call to the backend
  const api = useCallback(async (path, options = {}) => {
    const token = await user.getIdToken();
    const headers = { Authorization: `Bearer ${token}` };
    if (options.body && !(options.body instanceof FormData)) headers['Content-Type'] = 'application/json';
    const response = await fetch(`${BASE_URL}${path}`, { ...options, headers });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.error || `Error ${response.status}`);
    return data;
  }, [user]);

  // Transcription models offered by the backend (OPENROUTER_MODELS on Render)
  useEffect(() => {
    fetch(`${BASE_URL}/models/`)
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (!data) return;
        setModels(data.models || []);
        setSelectedModel(data.default || '');
      })
      .catch(() => {});
  }, []);

  // Firestore real-time listener (progress of every document)
  useEffect(() => {
    if (!user) { setLoading(false); return; }

    const documentsRef = collection(db, 'users', user.uid, 'documents');
    const q = query(documentsRef, orderBy('upload_timestamp', 'desc'));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      setDocuments(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      setLoading(false);
    }, (error) => {
      console.error('Firestore listener error:', error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [user]);

  // Folders and the open folder (with its summary); refreshed whenever a document changes status
  const statusKey = documents.map((d) => `${d.id}:${d.processing_status}`).join(',');

  const loadFolders = useCallback(() => {
    api('/folders/').then((data) => setFolders(data.folders || [])).catch((e) => console.error(e));
  }, [api]);

  const loadFolder = useCallback(() => {
    if (!folderId) { setFolder(null); return; }
    api(`/folders/${folderId}/`).then(setFolder).catch((e) => console.error(e));
  }, [api, folderId]);

  useEffect(() => { if (user) loadFolders(); }, [user, loadFolders, statusKey]);
  useEffect(() => { if (user) loadFolder(); }, [user, loadFolder, statusKey]);

  // Extra analyses of the open folder, each with a model you pick
  const loadAnalyses = useCallback(() => {
    if (!folderId) { setAnalyses([]); return; }
    api(`/folders/${folderId}/analyses/`).then((data) => setAnalyses(data.analyses || [])).catch((e) => console.error(e));
  }, [api, folderId]);

  useEffect(() => { if (user) loadAnalyses(); }, [user, loadAnalyses]);

  const analysisRunning = analyses.some((a) => a.status === 'running');
  useEffect(() => {
    if (!analysisRunning) return undefined;
    const timer = setInterval(loadAnalyses, 5000);
    return () => clearInterval(timer);
  }, [analysisRunning, loadAnalyses]);

  const runAnalysis = async () => {
    const model = analysisModel || models.find((m) => m !== currentFolder?.summary_model) || models[0];
    if (!model) return;
    try {
      const created = await api(`/folders/${folderId}/analyses/`, {
        method: 'POST',
        body: JSON.stringify({ model, instructions: analysisInstructions.trim() }),
      });
      setAnalyses((list) => [created, ...list]);
      setAnalysisInstructions('');
    } catch (error) {
      alert('No se pudo iniciar el análisis: ' + error.message);
    }
  };

  const deleteAnalysis = async (analysis) => {
    if (!window.confirm(`¿Eliminar el análisis de ${analysis.model}?`)) return;
    try {
      await api(`/folders/${folderId}/analyses/${analysis.id}/`, { method: 'DELETE' });
      setAnalyses((list) => list.filter((a) => a.id !== analysis.id));
    } catch (error) {
      alert('No se pudo eliminar: ' + error.message);
    }
  };

  const summaryStatus = folder?.summary_status;
  useEffect(() => {
    if (summaryStatus !== 'generating') return undefined;
    const timer = setInterval(loadFolder, 5000);
    return () => clearInterval(timer);
  }, [summaryStatus, loadFolder]);

  const folderDocuments = documents
    .filter((doc) => (doc.folder_unique_id || 'default') === folderId)
    .sort((a, b) => naturalCompare(a.original_filename, b.original_filename));

  const filteredDocuments = folderDocuments.filter(doc =>
    !searchQuery || doc.original_filename?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const currentFolder = folder || folders.find((f) => f.id === folderId);
  const hasCompleted = folderDocuments.some((d) => d.processing_status === 'completed');

  const openFolder = (id) => {
    setSearchQuery('');
    setEditingName(false);
    setSearchParams(id ? { folder: id } : {});
  };

  const createFolder = async () => {
    try {
      const created = await api('/folders/', { method: 'POST', body: JSON.stringify({ name: newFolderName.trim() }) });
      setCreatingFolder(false);
      setNewFolderName('');
      loadFolders();
      openFolder(created.id);
    } catch (error) {
      alert('No se pudo crear el folder: ' + error.message);
    }
  };

  const renameFolder = async () => {
    try {
      await api(`/folders/${folderId}/`, { method: 'PATCH', body: JSON.stringify({ name: nameDraft.trim() }) });
      setEditingName(false);
      loadFolder();
      loadFolders();
    } catch (error) {
      alert('No se pudo cambiar el nombre: ' + error.message);
    }
  };

  const regenerateSummary = async () => {
    try {
      await api(`/folders/${folderId}/summary/`, { method: 'POST' });
      setFolder((f) => ({ ...(f || {}), summary_status: 'generating' }));
    } catch (error) {
      alert('No se pudo generar el resumen: ' + error.message);
    }
  };

  const getStatusIcon = useCallback((status) => {
    switch (status) {
      case 'completed':  return <CheckCircle className="h-4 w-4 text-emerald-500" />;
      case 'failed':     return <AlertCircle className="h-4 w-4 text-red-500" />;
      case 'uploading':
      case 'processing': return <Loader2 className="h-4 w-4 text-amber-500 animate-spin" />;
      default:           return <Clock className="h-4 w-4 text-gray-400" />;
    }
  }, []);

  const getStatusBadge = useCallback((status) => {
    const styles = {
      completed: 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200',
      failed:    'bg-red-50 text-red-700 ring-1 ring-red-200',
      uploading: 'bg-amber-50 text-amber-700 ring-1 ring-amber-200',
      processing:'bg-amber-50 text-amber-700 ring-1 ring-amber-200',
    };
    return styles[status] || 'bg-gray-50 text-gray-600 ring-1 ring-gray-200';
  }, []);

  const viewDocument = useCallback((doc) => {
    navigate(`/dashboard/${doc.document_id || doc.id}`);
  }, [navigate]);

  const deleteDocument = useCallback(async (doc, e) => {
    e.stopPropagation();
    if (!window.confirm(`¿Eliminar "${doc.original_filename}"? No se puede deshacer.`)) return;
    try {
      const token = await user.getIdToken();
      const response = await fetch(
        `${BASE_URL}/documents/${doc.document_id || doc.id}/delete/?uuid=${user.uid}`,
        { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (!response.ok) {
        const error = await response.json();
        alert('No se pudo eliminar: ' + (error.error || 'Error desconocido'));
      }
      // onSnapshot removes the row automatically
    } catch (error) {
      alert('No se pudo eliminar: ' + error.message);
    }
  }, [user]);

  const closeModal = useCallback(() => {
    setShowUploadModal(false);
    setUploadFiles([]);
  }, []);

  const handleUpload = useCallback(async () => {
    if (uploadFiles.length === 0) return;
    setIsUploading(true);

    const formData = new FormData();
    formData.append('uuid', user.uid);
    formData.append('folder_unique_id', folderId || 'default');
    if (selectedModel) formData.append('model', selectedModel);
    uploadFiles.forEach(file => formData.append('files', file));

    try {
      const token = await user.getIdToken();
      const response = await fetch(`${BASE_URL}/documents/upload/`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        alert('No se pudo subir: ' + (error.error || `Error ${response.status}`));
        setIsUploading(false);
        return;
      }

      setIsUploading(false);
      setUploadFiles([]);
      setShowUploadModal(false);
    } catch (error) {
      alert('No se pudo subir: ' + error.message);
      setIsUploading(false);
    }
  }, [uploadFiles, user, selectedModel, folderId]);

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-white text-gray-900">
      <style>{`
        @keyframes fadeIn  { from { opacity: 0; } to { opacity: 1; } }
        @keyframes slideUp { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }
        .animate-fadeIn  { animation: fadeIn  0.2s ease-out; }
        .animate-slideUp { animation: slideUp 0.3s ease-out; }
      `}</style>

      {loading ? (
        <div className="mx-auto max-w-6xl px-6 py-10">
          <div className="mb-8">
            <div className="h-8 w-48 bg-gray-200 rounded-lg animate-pulse mb-2" />
            <div className="h-4 w-32 bg-gray-100 rounded animate-pulse" />
          </div>
          <div className="border border-gray-200 rounded-2xl overflow-hidden bg-white divide-y divide-gray-100">
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
          </div>
        </div>

      ) : !folderId ? (
        /* ── Folder list ─────────────────────────────────────────────────── */
        <div className="mx-auto max-w-6xl px-6 py-10">
          <div className="flex items-start justify-between gap-4 mb-8">
            <div>
              <h2 className="text-2xl font-semibold text-gray-900">Folders</h2>
              <p className="text-gray-500 text-sm mt-1 max-w-xl">
                Un folder por libro, legajo o expediente. Sus imágenes se leen en orden, cada página con la anterior
                y la siguiente como contexto, y al final se escribe un resumen del folder.
              </p>
            </div>
            <button
              onClick={() => setCreatingFolder(true)}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-gray-900 text-white rounded-xl hover:bg-gray-800 transition-colors text-sm font-medium shadow-sm flex-shrink-0"
            >
              <FolderPlus className="h-4 w-4" />
              Nuevo folder
            </button>
          </div>

          {creatingFolder && (
            <div className="mb-6 flex flex-col sm:flex-row gap-3 p-4 border border-gray-200 rounded-2xl bg-gray-50">
              <input
                autoFocus
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') createFolder();
                  if (e.key === 'Escape') { setCreatingFolder(false); setNewFolderName(''); }
                }}
                placeholder="Nombre del folder (opcional)"
                className="flex-1 px-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-gray-200"
              />
              <button
                onClick={createFolder}
                className="px-5 py-2.5 bg-gray-900 text-white rounded-xl hover:bg-gray-800 text-sm font-medium"
              >
                Crear
              </button>
              <button
                onClick={() => { setCreatingFolder(false); setNewFolderName(''); }}
                className="px-5 py-2.5 text-sm font-medium text-gray-600 border border-gray-200 rounded-xl hover:bg-white"
              >
                Cancelar
              </button>
            </div>
          )}

          {folders.length === 0 ? (
            <div className="text-center py-20 border border-dashed border-gray-200 rounded-2xl">
              <Folder className="h-10 w-10 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-900 font-medium mb-1">Todavía no hay folders</p>
              <p className="text-sm text-gray-500">Crea un folder para cada libro y sube ahí sus imágenes.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {folders.map((f) => (
                <button
                  key={f.id}
                  onClick={() => openFolder(f.id)}
                  className="text-left p-5 border border-gray-200 rounded-2xl hover:border-gray-300 hover:bg-gray-50/60 transition-colors"
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center flex-shrink-0">
                      <Folder className="h-5 w-5 text-amber-600" />
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium text-gray-900 truncate">{folderTitle(f)}</p>
                      <p className="text-xs text-gray-400">{formatDate(f.created_at)}</p>
                    </div>
                  </div>
                  <p className="text-sm text-gray-500">
                    {f.document_count} documento{f.document_count !== 1 ? 's' : ''}
                    {f.processing_count ? ` · ${f.processing_count} procesando` : ''}
                    {f.failed_count ? ` · ${f.failed_count} con error` : ''}
                    {f.photo_count ? ` · ${f.photo_count} fotos de la app` : ''}
                  </p>
                  {f.summary_status && (
                    <p className={`text-xs mt-2 ${f.summary_status === 'failed' ? 'text-red-600' : 'text-amber-700'}`}>
                      {SUMMARY_LABELS[f.summary_status] || ''}
                    </p>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

      ) : (
        /* ── One folder ──────────────────────────────────────────────────── */
        <div className="mx-auto max-w-6xl px-6 py-10">
          <button
            onClick={() => openFolder(null)}
            className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-900 mb-4"
          >
            <ArrowLeft className="h-4 w-4" />
            Folders
          </button>

          <div className="flex items-start justify-between gap-4 mb-6">
            <div className="min-w-0 flex-1">
              {editingName ? (
                <div className="flex gap-2">
                  <input
                    autoFocus
                    value={nameDraft}
                    onChange={(e) => setNameDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') renameFolder();
                      if (e.key === 'Escape') setEditingName(false);
                    }}
                    placeholder="Nombre del folder"
                    className="flex-1 max-w-md px-3 py-2 border border-gray-200 rounded-xl text-lg focus:outline-none focus:ring-2 focus:ring-gray-200"
                  />
                  <button onClick={renameFolder} className="px-4 py-2 bg-gray-900 text-white rounded-xl text-sm font-medium">
                    Guardar
                  </button>
                </div>
              ) : (
                <h2 className="text-2xl font-semibold text-gray-900 flex items-center gap-2 min-w-0">
                  <span className="truncate">{folderTitle(currentFolder)}</span>
                  {folderId !== 'default' && (
                    <button
                      onClick={() => { setNameDraft(currentFolder?.name || ''); setEditingName(true); }}
                      title="Cambiar nombre"
                      className="p-1 text-gray-400 hover:text-gray-700 flex-shrink-0"
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                  )}
                </h2>
              )}
              <p className="text-gray-500 text-sm mt-1">
                {folderDocuments.length} documento{folderDocuments.length !== 1 ? 's' : ''} · ordenados por nombre de archivo
              </p>
            </div>
            <button
              onClick={() => setShowUploadModal(true)}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-gray-900 text-white rounded-xl hover:bg-gray-800 transition-colors text-sm font-medium shadow-sm flex-shrink-0"
            >
              <Upload className="h-4 w-4" />
              Subir imágenes
            </button>
          </div>

          {/* Folder summary */}
          <div className="mb-8 border border-gray-200 rounded-2xl p-5 bg-gray-50/60">
            <div className="flex items-center justify-between gap-3 mb-3">
              <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-amber-600" />
                Resumen del folder
              </h3>
              <button
                onClick={regenerateSummary}
                disabled={summaryStatus === 'generating' || !hasCompleted}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <RefreshCw className={`h-3.5 w-3.5 ${summaryStatus === 'generating' ? 'animate-spin' : ''}`} />
                {currentFolder?.summary ? 'Regenerar' : 'Generar'}
              </button>
            </div>
            {summaryStatus === 'generating' ? (
              <p className="text-sm text-amber-700 flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                Escribiendo el resumen con todas las transcripciones del folder…
              </p>
            ) : currentFolder?.summary ? (
              <>
                <pre className="whitespace-pre-wrap font-sans text-sm text-gray-800 leading-relaxed">{currentFolder.summary}</pre>
                <p className="text-xs text-gray-400 mt-3">
                  {currentFolder.summary_documents} documento{currentFolder.summary_documents !== 1 ? 's' : ''}
                  {currentFolder.summary_model ? ` · ${currentFolder.summary_model}` : ''}
                  {currentFolder.summary_updated_at ? ` · ${formatDate(currentFolder.summary_updated_at)}` : ''}
                </p>
              </>
            ) : summaryStatus === 'failed' ? (
              <p className="text-sm text-red-600">No se pudo escribir el resumen: {currentFolder?.summary_error}</p>
            ) : (
              <p className="text-sm text-gray-500">
                El resumen se escribe solo cuando termina de transcribirse lo que subes a este folder.
              </p>
            )}
          </div>

          {/* Extra analyses with other models */}
          <div className="mb-8 border border-gray-200 rounded-2xl p-5">
            <h3 className="font-semibold text-gray-900 flex items-center gap-2 mb-1">
              <Bot className="h-4 w-4 text-gray-500" />
              Análisis con otros modelos
            </h3>
            <p className="text-sm text-gray-500 mb-4">
              Otro modelo analiza las transcripciones de este folder por separado. No reemplaza el resumen principal.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 mb-3">
              <select
                value={analysisModel || models.find((m) => m !== currentFolder?.summary_model) || models[0] || ''}
                onChange={(e) => setAnalysisModel(e.target.value)}
                className="sm:w-72 px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-200"
              >
                {models.map((m) => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
              <button
                onClick={runAnalysis}
                disabled={!hasCompleted || models.length === 0}
                className="inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-gray-900 text-white rounded-xl hover:bg-gray-800 text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Play className="h-4 w-4" />
                Ejecutar análisis
              </button>
            </div>
            <textarea
              value={analysisInstructions}
              onChange={(e) => setAnalysisInstructions(e.target.value)}
              rows={2}
              placeholder="Instrucciones opcionales, ej.: busca todos los nombres de caciques y quipucamayocs, o compara con el resumen principal"
              className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-200"
            />
            {!hasCompleted && (
              <p className="text-xs text-gray-400 mt-2">Disponible cuando haya al menos una transcripción terminada.</p>
            )}

            {analyses.length > 0 && (
              <div className="mt-5 space-y-4">
                {analyses.map((a) => (
                  <div key={a.id} className="border border-gray-200 rounded-xl p-4 bg-gray-50/60">
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{a.model_used || a.model}</p>
                        <p className="text-xs text-gray-400">
                          {formatDate(a.created_at)}
                          {a.documents ? ` · ${a.documents} documento${a.documents !== 1 ? 's' : ''}` : ''}
                        </p>
                        {a.instructions && (
                          <p className="text-xs text-gray-500 mt-1 italic">“{a.instructions}”</p>
                        )}
                      </div>
                      <button
                        onClick={() => deleteAnalysis(a)}
                        title="Eliminar análisis"
                        className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg flex-shrink-0"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                    {a.status === 'running' ? (
                      <p className="text-sm text-amber-700 flex items-center gap-2">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Analizando…
                      </p>
                    ) : a.status === 'failed' ? (
                      <p className="text-sm text-red-600">Falló: {a.error}</p>
                    ) : (
                      <pre className="whitespace-pre-wrap font-sans text-sm text-gray-800 leading-relaxed">{a.result}</pre>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {folderDocuments.length === 0 ? (
            <div className="text-center py-16 border border-dashed border-gray-200 rounded-2xl">
              <FileText className="h-10 w-10 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-900 font-medium mb-1">Este folder está vacío</p>
              <p className="text-sm text-gray-500 mb-6">Sube las imágenes del libro. Se leen en orden de nombre de archivo.</p>
              <button
                onClick={() => setShowUploadModal(true)}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-gray-900 text-white rounded-xl hover:bg-gray-800 text-sm font-medium"
              >
                <Upload className="h-4 w-4" />
                Subir imágenes
              </button>
            </div>
          ) : (
            <>
              {folderDocuments.length > 3 && (
                <div className="relative mb-6">
                  <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Buscar por nombre de archivo..."
                    className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-200 focus:border-gray-300 transition-all"
                  />
                </div>
              )}

              <div className="border border-gray-200 rounded-2xl overflow-hidden bg-white">
                <div className="grid grid-cols-[1fr_120px_100px_140px_100px] gap-4 px-6 py-3 bg-gray-50 border-b border-gray-200 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <span>Name</span>
                  <span>Date</span>
                  <span>Size</span>
                  <span>Status</span>
                  <span className="text-right">Action</span>
                </div>
                <div className="divide-y divide-gray-100">
                  {filteredDocuments.map((doc) => (
                    <DocumentRow
                      key={doc.document_id || doc.id}
                      doc={doc}
                      onView={viewDocument}
                      onDelete={deleteDocument}
                      getStatusIcon={getStatusIcon}
                      getStatusBadge={getStatusBadge}
                    />
                  ))}
                </div>
                {filteredDocuments.length === 0 && (
                  <div className="px-6 py-16 text-center">
                    <Search className="h-8 w-8 text-gray-300 mx-auto mb-3" />
                    <p className="text-sm text-gray-500">Nada coincide con "{searchQuery}"</p>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {/* ── Upload modal (inlined — no remount) ────────────────────────────── */}
      {showUploadModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm animate-fadeIn"
          onClick={(e) => { if (e.target === e.currentTarget && !isUploading) closeModal(); }}
        >
          <div className="mx-4 max-w-xl w-full bg-white rounded-2xl shadow-2xl p-6 animate-slideUp">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-900 truncate">Subir a {folderTitle(currentFolder)}</h2>
              {!isUploading && (
                <button
                  onClick={closeModal}
                  className="p-2 rounded-xl hover:bg-gray-100 transition-colors text-gray-500"
                >
                  <X className="h-5 w-5" />
                </button>
              )}
            </div>

            {isUploading ? (
              /* ── Uploading state — shown for 2s then auto-closes ── */
              <div className="py-12 text-center">
                <div className="w-14 h-14 rounded-2xl bg-amber-50 flex items-center justify-center mx-auto mb-4">
                  <Loader2 className="h-7 w-7 text-amber-500 animate-spin" />
                </div>
                <p className="text-base font-semibold text-gray-900 mb-1">
                  Subiendo {uploadFiles.length} archivo{uploadFiles.length !== 1 ? 's' : ''}…
                </p>
                <p className="text-sm text-gray-500">
                  Luego se transcriben en orden; verás el avance en la lista.
                </p>
              </div>
            ) : (
              <>
                {/* Drop zone */}
                <div
                  className={`border-2 border-dashed rounded-2xl p-10 text-center transition-all cursor-pointer ${
                    dragOver
                      ? 'border-blue-400 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  }`}
                  onClick={() => fileInputRef.current?.click()}
                  onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={(e) => {
                    e.preventDefault();
                    setDragOver(false);
                    setUploadFiles(prev => [...prev, ...Array.from(e.dataTransfer.files)]);
                  }}
                >
                  <div className="w-12 h-12 rounded-2xl bg-gray-100 flex items-center justify-center mx-auto mb-4">
                    <Upload className="h-6 w-6 text-gray-500" />
                  </div>
                  <p className="text-sm font-medium text-gray-900 mb-1">
                    Drop files here or click to browse
                  </p>
                  <p className="text-xs text-gray-500">PDF, JPG, PNG, HEIC (iPhone), TIFF — up to 50MB each</p>
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept=".pdf,.jpg,.jpeg,.png,.heic,.heif,.webp,.bmp,.tif,.tiff"
                    onChange={(e) => setUploadFiles(prev => [...prev, ...Array.from(e.target.files)])}
                    className="hidden"
                  />
                </div>

                {/* File list */}
                {uploadFiles.length > 0 && (
                  <div className="mt-4 space-y-2 max-h-40 overflow-y-auto">
                    {uploadFiles.map((file, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-xl group animate-fadeIn"
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <FileText className="h-4 w-4 text-gray-400 flex-shrink-0" />
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">{file.name}</p>
                            <p className="text-xs text-gray-500">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
                          </div>
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setUploadFiles(uploadFiles.filter((_, i) => i !== index));
                          }}
                          className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors opacity-0 group-hover:opacity-100"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Model */}
                {models.length > 0 && (
                  <label className="mt-4 flex items-center justify-between gap-3 text-sm text-gray-600">
                    <span>Transcription model</span>
                    <select
                      value={selectedModel}
                      onChange={(e) => setSelectedModel(e.target.value)}
                      className="flex-1 max-w-xs px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-200"
                    >
                      {models.map((m) => (
                        <option key={m} value={m}>{m}</option>
                      ))}
                    </select>
                  </label>
                )}

                {/* Buttons */}
                <div className="flex gap-3 mt-6">
                  <button
                    onClick={handleUpload}
                    disabled={uploadFiles.length === 0}
                    className="flex-1 flex items-center justify-center gap-2 py-3 bg-gray-900 text-white rounded-xl hover:bg-gray-800 transition-colors disabled:opacity-40 disabled:cursor-not-allowed text-sm font-medium"
                  >
                    <Upload className="h-4 w-4" />
                    Upload {uploadFiles.length > 0 ? `${uploadFiles.length} file${uploadFiles.length > 1 ? 's' : ''}` : ''}
                  </button>
                  <button
                    onClick={closeModal}
                    className="px-5 py-3 text-sm font-medium text-gray-600 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}